import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiMapPin, FiNavigation, FiCheck, FiPackage, 
  FiExternalLink, FiMap, FiWifi, FiShield, FiCamera, FiAlertTriangle
} from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency, getStatusLabel } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import Modal from '@/components/common/Modal';
import LeafletMap from '@/components/common/LeafletMap';
import wsService from '@/services/websocket';
import { useAuth } from '@/contexts/AuthContext';
import HandshakeScanner from '@/components/handshake/HandshakeScanner';
import OrderHandshakeQR from '@/components/handshake/OrderHandshakeQR';
import DeliveryPhotoCapture from '@/components/handshake/DeliveryPhotoCapture';

const DeliveryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [isSendingLocation, setIsSendingLocation] = useState(false);
  
  // Handshake states
  const [handshake, setHandshake] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [currentScanStep, setCurrentScanStep] = useState(null);
  const [showHandshakeStatus, setShowHandshakeStatus] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
      
      const storeResponse = await api.get(`/stores/${response.data.store_id}`);
      setStore(storeResponse.data);
      
      // Carregar dados de handshake
      try {
        const handshakeRes = await api.get(`/orders/${id}/handshake`);
        setHandshake(handshakeRes.data);
      } catch (e) {
        console.log('Handshake data not available');
      }
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  // Get and track current location
  useEffect(() => {
    if (!navigator.geolocation) return;
    
    // Initial location
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentLocation(loc);
      },
      (error) => console.error('Error getting location:', error),
      { enableHighAccuracy: true }
    );

    // Watch location continuously
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setCurrentLocation(loc);
      },
      (error) => console.error('Error watching location:', error),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // Send location updates via WebSocket
  useEffect(() => {
    if (!token || !currentLocation || !order || order.status !== 'em_rota') return;
    
    // Connect WebSocket if not connected
    wsService.connect(token);
    
    // Send location update
    const sendUpdate = () => {
      if (wsService.ws?.readyState === WebSocket.OPEN) {
        setIsSendingLocation(true);
        wsService.sendLocationUpdate({
          location: currentLocation,
          order_id: id
        });
        setTimeout(() => setIsSendingLocation(false), 500);
      }
    };
    
    // Send immediately
    sendUpdate();
    
    // Also update via REST as backup
    api.put('/driver/location', currentLocation, { params: { order_id: id } })
      .catch(e => console.log('REST location update failed'));
    
    // Send every 5 seconds
    const interval = setInterval(sendUpdate, 5000);
    
    return () => clearInterval(interval);
  }, [token, currentLocation, order?.status, id]);

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/orders/${id}/status`, { status: newStatus });
      loadOrder();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const handleCompleteDelivery = async () => {
    setSubmitting(true);
    setCodeError('');
    
    try {
      await api.post(`/orders/${id}/complete`, { security_code: securityCode });
      setShowCodeModal(false);
      navigate('/entregador');
    } catch (error) {
      setCodeError(error.response?.data?.detail || 'Código inválido');
    } finally {
      setSubmitting(false);
    }
  };

  const openGoogleMaps = () => {
    const destination = order.status === 'coleta' 
      ? store?.coordinates 
      : order?.delivery_coordinates;
    
    if (destination) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`, '_blank');
    }
  };

  const openWaze = () => {
    const destination = order.status === 'coleta' 
      ? store?.coordinates 
      : order?.delivery_coordinates;
    
    if (destination) {
      window.open(`https://waze.com/ul?ll=${destination.lat},${destination.lng}&navigate=yes`, '_blank');
    }
  };

  // Handshake - Iniciar escaneamento
  const startHandshakeScan = (step) => {
    setCurrentScanStep(step);
    setShowScanner(true);
  };

  // Handshake - Sucesso do scan
  const handleScanSuccess = (result) => {
    setShowScanner(false);
    setCurrentScanStep(null);
    loadOrder(); // Recarregar dados atualizados
  };

  // Determinar próximo passo do handshake
  const getNextHandshakeStep = () => {
    if (!handshake) return null;
    
    if (order?.status === 'coleta') {
      if (!handshake.pickup_store_scanned) {
        return { step: 'pickup_store', label: 'Escanear QR da Loja', icon: '📍' };
      }
      if (!handshake.pickup_product_scanned) {
        return { step: 'pickup_product', label: 'Escanear QR do Produto', icon: '📦' };
      }
    }
    
    if (order?.status === 'em_rota') {
      if (!handshake.dropoff_arrived) {
        return { step: 'dropoff_arrive', label: 'Confirmar Chegada', icon: '📍' };
      }
      if (!handshake.dropoff_client_verified) {
        return { step: 'dropoff_client', label: 'Verificar Cliente', icon: '👤' };
      }
      if (!handshake.dropoff_product_verified) {
        return { step: 'waiting_client', label: 'Aguardando Cliente Confirmar', icon: '⏳' };
      }
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!order || !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Pedido não encontrado</p>
      </div>
    );
  }

  const isPickup = order.status === 'coleta';
  const isEnRoute = order.status === 'em_rota';
  const destination = isPickup ? store : { name: order.client_name, address: order.delivery_address };
  const nextStep = getNextHandshakeStep();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="delivery-detail">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-700 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/entregador')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold">Entrega #{order.id.slice(0, 8)}</h1>
            <p className="text-sm text-orange-100">{getStatusLabel(order.status)}</p>
          </div>
          {/* Location Status */}
          {isEnRoute && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
              isSendingLocation ? 'bg-green-500/30' : 'bg-white/20'
            }`}>
              <FiWifi className={isSendingLocation ? 'animate-pulse' : ''} />
              <span>GPS {isSendingLocation ? 'enviando' : 'ativo'}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Status Banner */}
        <div className={`rounded-xl p-4 ${isPickup ? 'bg-yellow-50 border-2 border-yellow-300' : 'bg-blue-50 border-2 border-blue-300'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isPickup ? 'bg-yellow-500' : 'bg-blue-500'} text-white`}>
              {isPickup ? <FiPackage className="text-xl" /> : <FiNavigation className="text-xl" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                {isPickup ? 'Colete na Loja' : 'Entregue ao Cliente'}
              </h2>
              <p className="text-sm text-gray-600">
                {isPickup ? 'Vá até a loja para retirar o pedido' : 'Leve o pedido até o endereço do cliente'}
              </p>
            </div>
          </div>
        </div>

        {/* Handshake Digital - Próximo Passo */}
        {nextStep && nextStep.step !== 'waiting_client' && (
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-2xl">
                  {nextStep.icon}
                </div>
                <div>
                  <p className="text-sm text-green-100">Próximo passo:</p>
                  <h3 className="font-bold text-lg">{nextStep.label}</h3>
                </div>
              </div>
              <button
                onClick={() => startHandshakeScan(nextStep.step)}
                className="px-4 py-3 bg-white text-green-600 rounded-xl font-bold hover:bg-green-50 flex items-center gap-2"
                data-testid={`handshake-btn-${nextStep.step}`}
              >
                <FiShield />
                Iniciar
              </button>
            </div>
          </div>
        )}

        {/* Aguardando cliente */}
        {nextStep?.step === 'waiting_client' && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-2xl animate-pulse">
                ⏳
              </div>
              <div>
                <h3 className="font-bold text-yellow-800">Aguardando Cliente</h3>
                <p className="text-sm text-yellow-700">
                  O cliente precisa confirmar o recebimento do produto
                </p>
              </div>
            </div>
            
            {/* QR do produto para mostrar ao cliente */}
            <div className="mt-4 bg-white rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-3">
                Mostre este QR Code ou PIN para o cliente confirmar:
              </p>
              {handshake?.product_qr_token && (
                <div className="space-y-3">
                  <OrderHandshakeQR orderId={id} userType="entregador" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status do Handshake */}
        {handshake && (
          <button
            onClick={() => setShowHandshakeStatus(!showHandshakeStatus)}
            className="w-full bg-white rounded-xl p-4 shadow-sm text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiShield className="text-green-500" />
                <span className="font-medium text-gray-800">Verificação de Segurança</span>
              </div>
              <span className="text-sm text-gray-500">
                {showHandshakeStatus ? 'Ocultar' : 'Ver detalhes'}
              </span>
            </div>
            
            {showHandshakeStatus && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-gray-600 mb-2">Progresso:</p>
                {[
                  { key: 'pickup_store_scanned', label: '1. QR da loja escaneado' },
                  { key: 'pickup_product_scanned', label: '2. QR do produto escaneado' },
                  { key: 'dropoff_arrived', label: '3. Chegada confirmada' },
                  { key: 'dropoff_client_verified', label: '4. Cliente verificado' },
                  { key: 'dropoff_product_verified', label: '5. Produto confirmado' }
                ].map(step => (
                  <div key={step.key} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                      handshake[step.key] ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {handshake[step.key] ? '✓' : ''}
                    </span>
                    <span className={`text-sm ${handshake[step.key] ? 'text-green-700' : 'text-gray-500'}`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </button>
        )}

        {/* Destination Card */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            {isPickup ? '📦 Retirar em' : '📍 Entregar em'}
          </h3>
          <p className="text-lg font-semibold text-gray-800">{destination.name}</p>
          <p className="text-gray-600 mt-1">{destination.address || store.address}</p>
        </div>

        {/* Navigation Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={openGoogleMaps} 
            className="flex items-center justify-center gap-2 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700"
            data-testid="google-maps-btn"
          >
            <FiNavigation /> Google Maps
          </button>
          <button 
            onClick={openWaze} 
            className="flex items-center justify-center gap-2 py-4 bg-cyan-500 text-white rounded-xl font-semibold hover:bg-cyan-600"
            data-testid="waze-btn"
          >
            <FiExternalLink /> Waze
          </button>
        </div>

        {/* Embedded Map */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={() => setShowMap(!showMap)}
            className="w-full p-3 flex items-center justify-between text-gray-700 hover:bg-gray-50"
          >
            <span className="flex items-center gap-2 font-medium">
              <FiMap className="text-blue-600" /> Mapa da Rota
            </span>
            <span className="text-sm text-gray-500">{showMap ? 'Ocultar' : 'Mostrar'}</span>
          </button>
          {showMap && (
            <LeafletMap
              storeLocation={store.coordinates}
              clientLocation={order.delivery_coordinates}
              driverLocation={currentLocation}
              showRoute={true}
              height="300px"
            />
          )}
        </div>

        {/* Earnings Card */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h3 className="text-sm font-medium text-green-700 mb-2">💰 Valor da Entrega</h3>
          <p className="text-3xl font-bold text-green-600">{formatCurrency(order.driver_amount || 10)}</p>
        </div>

        {/* Order Items */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-medium text-gray-800 mb-3">Itens do Pedido</h3>
          <div className="space-y-2">
            {order.items.map((item, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-600">{item.quantity}x {item.product_name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons - Fallback sem Handshake */}
        {!handshake && (
          <div className="sticky bottom-0 bg-gray-50 py-4 -mx-4 px-4 border-t">
            {isPickup && (
              <button
                onClick={() => updateStatus('em_rota')}
                className="w-full py-4 bg-orange-500 text-white rounded-xl font-semibold text-lg hover:bg-orange-600 flex items-center justify-center gap-2"
                data-testid="start-delivery-btn"
              >
                <FiNavigation /> Iniciar Entrega
              </button>
            )}
            
            {isEnRoute && (
              <button
                onClick={() => setShowCodeModal(true)}
                className="w-full py-4 bg-green-500 text-white rounded-xl font-semibold text-lg hover:bg-green-600 flex items-center justify-center gap-2"
                data-testid="complete-delivery-btn"
              >
                <FiCheck /> Confirmar Entrega
              </button>
            )}
          </div>
        )}

        {/* Botão finalizar - Quando handshake completo */}
        {handshake?.dropoff_product_verified && (
          <div className="sticky bottom-0 bg-gray-50 py-4 -mx-4 px-4 border-t">
            <button
              onClick={() => setShowPhotoCapture(true)}
              className="w-full py-4 bg-green-500 text-white rounded-xl font-semibold text-lg hover:bg-green-600 flex items-center justify-center gap-2"
              data-testid="finalize-delivery-btn"
            >
              <FiCamera /> Tirar Foto e Finalizar
            </button>
            <p className="text-center text-sm text-gray-500 mt-2">
              Tire uma foto do pacote junto ao número da casa
            </p>
          </div>
        )}
      </main>

      {/* Security Code Modal */}
      <Modal isOpen={showCodeModal} onClose={() => setShowCodeModal(false)} title="Código de Segurança">
        <div className="p-4">
          <p className="text-gray-600 mb-4">
            Digite o código de 6 dígitos fornecido pelo cliente para confirmar a entrega.
          </p>
          <input
            type="text"
            value={securityCode}
            onChange={(e) => setSecurityCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full text-center text-3xl font-bold tracking-[0.5em] py-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            maxLength={6}
            data-testid="security-code-input"
          />
          {codeError && (
            <p className="text-red-500 text-sm mt-2 text-center">{codeError}</p>
          )}
          <button
            onClick={handleCompleteDelivery}
            disabled={securityCode.length !== 6 || submitting}
            className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="confirm-delivery-btn"
          >
            {submitting ? 'Verificando...' : 'Confirmar Entrega'}
          </button>
        </div>
      </Modal>

      {/* Handshake Scanner */}
      {showScanner && (
        <HandshakeScanner
          orderId={id}
          step={currentScanStep}
          storeLocation={store?.coordinates}
          clientLocation={order?.delivery_coordinates}
          onSuccess={handleScanSuccess}
          onCancel={() => setShowScanner(false)}
        />
      )}

      {/* Captura de Foto da Entrega */}
      {showPhotoCapture && (
        <DeliveryPhotoCapture
          orderId={id}
          driverLocation={currentLocation}
          onSuccess={() => {
            setShowPhotoCapture(false);
            navigate('/entregador');
          }}
          onCancel={() => setShowPhotoCapture(false)}
        />
      )}
    </div>
  );
};

export default DeliveryDetail;
